import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { PaziaButton, PaziaInput, PaziaSelect } from "components/atoms";
import { ATTRIBUTE_TYPE_OPTIONS, CourseGroupAttributeApi, CourseGroupAttributeType } from "api/coursegroup-attributes";

const optionSchema = yup.object({
  id: yup.number().optional(),
  value: yup.string().required("Wartość jest wymagana"),
  numericValue: yup
    .number()
    .nullable()
    .optional()
    .transform((value, originalValue) => (originalValue === "" ? null : value)),
  sortOrder: yup.number().required().default(0),
  color: yup
    .string()
    .nullable()
    .optional()
    .transform((value, originalValue) => (originalValue === "" ? null : value)),
});

const schema = yup.object({
  name: yup.string().required("Nazwa jest wymagana"),
  attributeType: yup.number().required("Typ jest wymagany"),
  options: yup
    .array()
    .of(optionSchema)
    .when("attributeType", {
      is: (val: number) =>
        val === CourseGroupAttributeType.DictionarySingleSelect ||
        val === CourseGroupAttributeType.DictionaryMultiSelect,
      then: (s) => s.min(1, "Dodaj przynajmniej jedną opcję"),
      otherwise: (s) => s.optional(),
    }),
});

export type CourseGroupAttributeFormValues = yup.InferType<typeof schema>;

interface CourseGroupAttributeFormProps {
  attribute?: CourseGroupAttributeApi | null;
  onSubmit: (data: CourseGroupAttributeFormValues) => void;
}

export const CourseGroupAttributeForm = ({ attribute, onSubmit }: CourseGroupAttributeFormProps) => {
  const methods = useForm<CourseGroupAttributeFormValues>({
    resolver: yupResolver(schema),
    defaultValues: attribute
      ? {
          name: attribute.name,
          attributeType: attribute.attributeType,
          options: attribute.options.map((o) => ({
            id: o.id,
            value: o.value,
            numericValue: o.numericValue,
            sortOrder: o.sortOrder,
            color: o.color ?? "",
          })),
        }
      : {
          name: "",
          attributeType: undefined,
          options: [],
        },
  });

  const { handleSubmit, control, register, formState, setValue } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "options",
  });

  console.log("Form errors:", formState.errors);
  const attributeType = useWatch({ control, name: "attributeType" });
  const isDictionary =
    +attributeType === CourseGroupAttributeType.DictionarySingleSelect ||
    +attributeType === CourseGroupAttributeType.DictionaryMultiSelect;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid min-w-0 max-w-xl gap-2 pt-4">
        <PaziaInput name="name" label="Nazwa" type="text" />
        <PaziaSelect label="Typ" name="attributeType" options={ATTRIBUTE_TYPE_OPTIONS} />

        {isDictionary && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Opcje</h3>
            {fields.map((field, index) => (
              // Phones: value on its own row, the rest wrapped two per row inside a bordered block
              <div
                key={field.id}
                className="mb-2 grid grid-cols-2 items-end gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700 sm:flex sm:rounded-none sm:border-0 sm:p-0"
              >
                <div className="col-span-2 sm:flex-1">
                  <PaziaInput name={`options.${index}.value`} label="Wartość" type="text" />
                </div>
                <div className="sm:w-32">
                  <PaziaInput name={`options.${index}.numericValue`} label="Wartość num." type="number" />
                </div>
                <div className="sm:w-24">
                  <PaziaInput name={`options.${index}.sortOrder`} label="Kolejność" type="number" />
                </div>
                <div className="sm:w-20">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Kolor</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      className="h-8 w-8 cursor-pointer rounded border border-slate-300"
                      value={methods.watch(`options.${index}.color`) || "#000000"}
                      onChange={(e) => methods.setValue(`options.${index}.color`, e.target.value)}
                    />
                    {methods.watch(`options.${index}.color`) && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-red-500"
                        onClick={() => methods.setValue(`options.${index}.color`, "")}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                <div className="pb-1">
                  <PaziaButton title="Usuń" type="danger" onClick={() => remove(index)} />
                </div>
              </div>
            ))}
            <PaziaButton
              title="Dodaj opcję"
              type="secondary"
              onClick={() => append({ value: "", numericValue: null, sortOrder: fields.length, color: "" })}
            />
          </div>
        )}

        <div className="pt-4">
          <PaziaButton title="Zapisz" type="success" />
        </div>
      </form>
    </FormProvider>
  );
};
